import {
  ButtonHTMLAttributes,
  MutableRefObject,
  ReactElement,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Popover } from '@base-ui/react/popover';

import useObjectVisibility from '@hooks/useObjectVisibility';
import { trackHotspotView } from '_utils/GA4/trackHotspotEvents';
import HotspotDot from '../HotspotDot';
import ProductTile from '../ProductTile';
import type { ImageHotspotDot as ImageHotspotDotData } from '@adapters/prismic/transforms/imageHotspot';

import styles from './HotspotPopover.module.scss';

/** Mirrors `--component-spacing-16` — Base UI's `sideOffset`/`collisionPadding`
    take plain numbers (px), not CSS vars (same reason QuickAddPopover/index.tsx
    hardcodes `TRIGGER_HEIGHT = 48`). */
const TILE_OFFSET = 16;

/** True for Base UI dismiss reasons that mean "something else stole
    focus/a click", as opposed to an explicit request to close this popover
    (trigger re-press, Escape, its own close button). */
const isIncidentalClose = (reason: string | undefined): boolean =>
  reason === 'outside-press' || reason === 'focus-out';

export interface HotspotPopoverProps {
  hotspot: ImageHotspotDotData;
  open: boolean;
  /**
   * `eventDetails` (when Base UI supplies it) is forwarded through so the
   * caller can call `eventDetails.cancel()` when it decides to veto a
   * request (`ImageHotspot/index.tsx` does this while another hotspot is
   * pending) — without it, Base UI's internal store still registers the
   * trigger press as a legit open/close (`PopoverStore.setOpen` only skips
   * that update when `eventDetails.isCanceled`), leaving the dot stuck
   * showing its `data-popup-open` "active" styling even though it never
   * actually opened.
   */
  onOpenChange: (
    open: boolean,
    eventDetails?: { reason?: string; cancel: () => void }
  ) => void;
  /** Confines the tile to the photo instead of the whole viewport/page
      (Base UI's `collisionBoundary`) — the `.imageWrapper` ref from
      `ImageHotspot/index.tsx`. */
  collisionBoundary: Element | null;
  /** Forwarded to `ProductTile`'s own `onQuickAddPendingChange` — see its
      doc for why `ImageHotspot/index.tsx` needs this signal. */
  onQuickAddPendingChange?: (pending: boolean) => void;
  /** ED-416 (2) / ED-447 (1): the page/tab context `hotspot_view` and (via
      `ProductTile`) `click_hotspot_quick_atc` need but this component has
      no other way to know — `ImageHotspot/index.tsx` owns `asPath` and the
      active tab. Excludes the hotspot's own `link_text`/`link_url`/`index`,
      which this component already has (`hotspot`, `index` below). */
  trackingContext: {
    interactionLocation: string;
    tabName: string;
    coOpBrand: string | null;
  };
  /** 0-based position of this hotspot within its tab (top-to-bottom, then
      left-to-right) — shared by `hotspot_view` and `ImageHotspot/index.tsx`'s
      own `click_hotspot`. */
  index: number;
}

/**
 * A single hotspot: `HotspotDot` as the trigger, `ProductTile` as Base UI
 * Popover content anchored to it. Replaces the previous hand-rolled
 * positioning (`x`/`y`/`align` props on `ProductTile`, a breakpoint-scoped
 * `!important` CSS override for mobile, and a binary `x > 50` left/right
 * heuristic) with real anchor positioning: `side="right"` is a preference,
 * not a rule — Base UI's default collision avoidance flips to `left` and
 * shifts along either axis to keep the tile within `collisionBoundary`,
 * which also covers the "must stay near the dot, but never get cut off"
 * requirement mobile needed a special case for before.
 *
 * `open`/`onOpenChange` are owned by `ImageHotspot/index.tsx` (a single
 * `openHotspot` state shared across every dot, so opening one closes any
 * other that's open) rather than each popover managing its own — same
 * "controlled from the orchestrator" shape `QuickAdd`'s own popover uses.
 *
 * `ProductTile` composes its own nested `QuickAdd` popover (the cart
 * trigger). `QuickAdd`'s popup content portals to `document.body`, outside
 * this popover's own `Popup`/`.tile` DOM subtree — so from THIS popover's
 * dismiss logic, a click inside the (open) QuickAdd sheet reads as an
 * "outside" press and closes this one too, stranding the shopper mid quick-
 * add. `quickAddOpen` (fed by `ProductTile`'s `onQuickAddOpenChange`, itself
 * `QuickAdd`'s own `onOpenChange`) guards against exactly that: while it's
 * open, an incidental (outside-press/focus-out) close request for this
 * popover is cancelled — the same "cancel an incidental dismiss during a
 * sensitive sub-interaction" shape `QuickAdd/index.tsx`'s own
 * `handleOpenChange` already uses for its success/spinner views. An
 * explicit close (re-pressing the dot, Escape, switching tabs) still works,
 * since those aren't `outside-press`/`focus-out`.
 */
const HotspotPopover = ({
  hotspot,
  open,
  onOpenChange,
  collisionBoundary,
  onQuickAddPendingChange,
  trackingContext,
  index,
}: HotspotPopoverProps): ReactElement => {
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  // For `hotspot_view` (ED-416 (2)) below — a plain ref, separate from Base
  // UI's own trigger ref (merged onto `HotspotDot` in `render` below), since
  // `useObjectVisibility` needs a stable target it can hand straight to
  // `IntersectionObserver.observe`.
  const dotRef = useRef<Element | null>(null);
  const isDotVisible = useObjectVisibility(dotRef, 0.5, true);

  // Base UI hands `render` a new merged `ref` (buttonRef/registerTrigger/
  // triggerElementRef) on every render. Merging it into a fresh inline
  // callback each time would make React detach-then-reattach the trigger's
  // DOM node on every re-render of this component (e.g. every `quickAddOpen`
  // toggle) — including Base UI's own `registerTrigger`, which is what its
  // floating positioning is keyed on. `latestTriggerRef` lets `setMergedRef`
  // stay referentially stable via `useCallback(..., [])` while still always
  // calling whichever `triggerRef` Base UI most recently provided.
  const latestTriggerRef = useRef<Ref<HTMLButtonElement> | undefined>(
    undefined
  );
  const setMergedRef = useCallback((node: HTMLButtonElement | null) => {
    dotRef.current = node;
    const triggerRef = latestTriggerRef.current;
    if (typeof triggerRef === 'function') {
      triggerRef(node);
    } else if (triggerRef) {
      (triggerRef as MutableRefObject<HTMLButtonElement | null>).current = node;
    }
  }, []);

  useEffect(() => {
    if (!isDotVisible) return;
    trackHotspotView({
      data: {
        interactionLocation: trackingContext.interactionLocation,
        linkText: hotspot.product.title,
        linkUrl: hotspot.product.url,
        coOpBrand: trackingContext.coOpBrand,
        index,
      },
    });
    // `hotspot`/`trackingContext`/`index` intentionally excluded: this must
    // fire exactly once per mount, the moment visibility first crosses the
    // threshold, not again for an unrelated prop change (`ImageHotspot/
    // index.tsx` already forces a remount — and a fresh observer — whenever
    // this instance's underlying hotspot/tab/device identity changes, via
    // its own `key`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDotVisible]);

  const handleOpenChange = (
    nextOpen: boolean,
    eventDetails?: { reason?: string; cancel: () => void }
  ) => {
    if (!nextOpen && quickAddOpen && isIncidentalClose(eventDetails?.reason)) {
      eventDetails?.cancel();
      return;
    }
    onOpenChange(nextOpen, eventDetails);
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger
        render={(triggerProps) => {
          // Strip Base UI's native `type` so it can't collide with HotspotDot's
          // own `type="button"` — same defensive stripping
          // QuickAddPopover/index.tsx's `QuickAddPopoverTrigger` does. `ref`
          // is pulled out too, so it can be merged with `dotRef` via
          // `setMergedRef` above instead of being silently dropped by the
          // spread.
          const {
            type: _type,
            ref: triggerRef,
            ...rest
          } = triggerProps as ButtonHTMLAttributes<HTMLButtonElement> & {
            ref?: Ref<HTMLButtonElement>;
          };
          latestTriggerRef.current = triggerRef;
          return (
            <HotspotDot
              {...rest}
              ref={setMergedRef}
              x={hotspot.x}
              y={hotspot.y}
              label={hotspot.product.title}
            />
          );
        }}
      />
      <Popover.Portal>
        <Popover.Positioner
          side="right"
          align="center"
          sideOffset={TILE_OFFSET}
          collisionPadding={TILE_OFFSET}
          collisionBoundary={collisionBoundary ?? undefined}
          className={styles.positioner}
        >
          <Popover.Popup className={styles.popup}>
            <ProductTile
              product={hotspot.product}
              variantSku={hotspot.variantSku}
              onQuickAddOpenChange={setQuickAddOpen}
              onQuickAddPendingChange={onQuickAddPendingChange}
              interactionLocation={trackingContext.interactionLocation}
              tabName={trackingContext.tabName}
              coOpBrand={trackingContext.coOpBrand}
              index={index}
            />
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default HotspotPopover;
